/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */

import classNames from "classnames";
import { FontAwesomeIcon, Text } from "components";
import { Button } from "components/Button";
import Heading from "components/Heading";
import { InputStyles } from "components/Input/Input";
import { LoadingDots } from "components/Loaders";
import Stack from "components/Stack";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import useEventRouting from "hooks/useEventRouting";
import useTryCatch from "hooks/useTryCatch";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import VerificationInput from "react-verification-input";
import ROUTES from "routes";
import { useAuth } from "state/AuthContext";
import { useGlobal } from "state/GeneralContext";
import { useUser } from "state/UserContext";
import PageLayout from "templates/PageLayout";
import useLocalStorage from "utils/useLocalStorage";
import { sendSms, sendVerifyCode } from "../../lib/register-link";
import useRegisterLinkNavigation from "./useRegisterLinkNavigation";

export default function VerifyEmail({ visitor }) {
  const { t } = useTranslation();

  const [_token, setToken] = useLocalStorage(LOCAL_STORAGE_KEYS.token, null);
  const [_id, setId] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);
  const [_name, setName] = useLocalStorage(LOCAL_STORAGE_KEYS.name, null);
  const [userName] = useLocalStorage(LOCAL_STORAGE_KEYS.userName, null);
  const [_isActive, setIsActive] = useLocalStorage(
    LOCAL_STORAGE_KEYS.isActive,
    null,
  );
  const [eventCode] = useLocalStorage(LOCAL_STORAGE_KEYS.eventCode, null);
  const { navigateWithEvent } = useEventRouting();

  const { setEvents } = useGlobal();
  const { setUserData, setFormCompleted } = useUser();
  const { handleLogin } = useAuth();
  const loginNavigator = useRegisterLinkNavigation();
  const [isLoadingResend, setIsLoadingResend] = useState(false);
  const hasCodeBeenSent = useRef(false);

  useEffect(() => {
    loginNavigator();
  }, []);

  // code check
  const [code, setCode] = useState("");

  const resetCode = () => {
    setCode("");
  };
  const handleChange = (code) => {
    setCode(code);
  };

  // count down
  const [minutes, setMinutes] = useState(1);
  const [seconds, setSeconds] = useState(45);
  const [timeOut, settimeOut] = useState(false);
  const setTimestamp = (seconds = 105) => {
    const currentTime = moment();
    const futureTime = moment(currentTime)
      .add(seconds, "seconds")
      .toISOString();
    localStorage.setItem("sms-countdown", futureTime);
  };

  const clearTimeStamp = () => {
    localStorage.removeItem("sms-countdown");
  };

  // send code
  const sendVerificationCode = useCallback(async () => {
    if (!eventCode) {
      console.error("Event code is required");
      return;
    }
    const response = await sendSms({
      event_token: eventCode,
      username: userName,
    });

    if (response.ok) {
      toast.success(`verification code sent successfully`);
      setTimestamp(105);
      setSeconds(45);
      setMinutes(1);
      settimeOut(false);
      resetCode();
      hasCodeBeenSent.current = true;
    } else {
      toast.error(`Failed to send verification code`);
    }
  }, [eventCode, userName]);

  const reSendVerification = useTryCatch(async () => {
    await sendVerificationCode();
  }, setIsLoadingResend);

  useEffect(() => {
    const initializeVerification = async () => {
      // Only send if eventCode exists and hasn't been sent yet and user is not active
      if (eventCode && !hasCodeBeenSent.current && !_isActive) {
        await sendVerificationCode();
      }
    };
    initializeVerification();
  }, [eventCode]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (minutes === 0 && seconds === 0) {
        clearInterval(timer);
        settimeOut(true);
        resetCode();
        // Timer has reached zero; you can handle this event here.
      } else if (seconds === 0) {
        setMinutes(minutes - 1);
        setSeconds(59);
      } else {
        setSeconds(seconds - 1);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [minutes, seconds]);

  const [isLoading, setLoading] = useState(false);

  const handleOnSubmit = useTryCatch(async () => {
    if (!timeOut) {
      const { ok, data } = await sendVerifyCode({
        username: userName,
        code,
      });
      if (ok) {
        const { token, hash_id, first_name, last_name, user_events } = data;
        const targetEvent = user_events.find(
          (event) => event.event_code === eventCode,
        );
        if (!targetEvent) {
          return toast.error(
            "Couldn't find the event, please contact support.",
          );
        }

        setToken(token);
        setId(hash_id);
        setName(first_name);
        setIsActive(true);

        setFormCompleted(false);
        setUserData((prev) => ({
          ...prev,
          first_name,
          hash_id,
          last_name,
          is_form_completed: false,
        }));
        setEvents(user_events);
        handleLogin();
        navigateWithEvent(
          `/${targetEvent.id}${ROUTES.onboard.personalInfo(hash_id)}`,
        );
      }
    }
  }, setLoading);
  const goBack = () => {
    clearTimeStamp();
    localStorage.setItem([LOCAL_STORAGE_KEYS.registerPageStage], "signup");
    navigateWithEvent(ROUTES.registerLink.signup);
  };

  return (
    <PageLayout
      hasGoBack
      goBackMethod={goBack}
      title={t("registerLink.verifyEmail")}
      hasPolicy
      note="Please verify the information you have provided below and ensure it is accurate."
    >
      <VerifyComponent
        code={code}
        timeOut={timeOut}
        handleInputChange={handleChange}
        minutes={minutes}
        seconds={seconds}
        isLoadingResend={isLoadingResend}
        reSendVerification={reSendVerification}
        handleOnSubmit={handleOnSubmit}
        goBack={goBack}
        isLoading={isLoading}
      />
    </PageLayout>
  );
}

const VerifyComponent = ({
  code,
  timeOut,
  handleInputChange,
  minutes,
  seconds,
  isLoadingResend,
  reSendVerification,
  handleOnSubmit,
  goBack,
  isLoading,
}) => {
  const { t } = useTranslation();
  return (
    <Stack className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container px-6 py-10 gap-y-10 mt-10 md:px-[54px]">
      <Stack className="mb-5">
        <Heading as="h2" className="-text--theme-colors-text-title-brand">
          {t("guestInfoPage.identificationInfoLabel")}
        </Heading>
        <Text className="-text--theme-colors-text-body mt-3">
          {t("registrationVerifySMS.verificationInstruction")}{" "}
        </Text>
      </Stack>
      <div>
        <div className="flex gap-x-2 justify-center">
          <VerificationInput
            placeholder="-"
            autoFocus
            length={4}
            value={code}
            onChange={(value) => handleInputChange(value)}
            classNames={{
              container: "w-[240px]",
              characterInactive: classNames(InputStyles({ state: "locked" })),
              character: classNames(
                InputStyles({ state: "default" }),
                "-border--component-colors-field-default-input-border -text--component-colors-field-filled-input-text placeholder:-text--component-colors-field-default-input-placeholder",
                {
                  [InputStyles({ state: "error" })]: timeOut,
                },
              ),
            }}
          />
        </div>
        {timeOut && (
          <small className="-text--theme-colors-danger-default text-center block w-full">
            Invalid OTP, Try again or generate new OTP
          </small>
        )}
      </div>
      <div className="flex flex-col">
        <div className="flex justify-center items-center">
          <span className="-text--theme-colors-text-body font-bold">
            {minutes < 10 ? "0" : ""}
            {minutes}:{seconds < 10 ? "0" : ""}
            {seconds}
          </span>
          {isLoadingResend ? (
            <div className="w-[100px] flex justify-center">
              <LoadingDots />
            </div>
          ) : (
            <Button
              variant="ghost"
              size={"md"}
              isLoading={isLoadingResend}
              className="w-fit hover:bg-transparent"
              childClassName="flex justify-center gap-x-2 items-center"
              isDisabled={seconds || minutes}
              onClick={() => reSendVerification()}
            >
              <div className="flex items-center gap-x-2">
                <FontAwesomeIcon name="fa-arrow-rotate-right" />
                {t("registrationVerifySMS.resendCode")}
              </div>
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row justify-center items-center md:gap-x-8 mt-30 space-y-4 md:space-y-0">
        <div className="grow w-full">
          <Button
            variant="secondary"
            size="xl"
            className="font-bold"
            fullWidth
            onClick={goBack}
          >
            {t("guestInfoPage.backToEditLabel")}
          </Button>
        </div>
        <div className="grow w-full">
          <Button
            isLoading={isLoading}
            variant="primary"
            onClick={handleOnSubmit}
            size="xl"
            fullWidth
            className="font-bold"
          >
            <div className="flex items-center justify-center gap-x-s10 font-bold">
              {t("guestInfoPage.submitLabel")}
              <FontAwesomeIcon name="fa-chevron-right" />
            </div>
          </Button>
        </div>
      </div>
    </Stack>
  );
};
